import { BrokerStatus } from '@partner/shared/SharedConstants';
import { DashboardPeriodEcho, DashboardPeriodRequest } from './dashboard-filters.model';
import { DashboardVm } from './dashboard-vm.model';

/** Kept as the snapshot's own names for the shared wire shapes; every dashboard endpoint binds the same three. */
export { DashboardWireScope as ExecutiveSnapshotWireScope } from './dashboard-filters.model';
export type ExecutiveSnapshotRequest = DashboardPeriodRequest;
export type ExecutiveSnapshotPeriod = DashboardPeriodEcho;

export interface FundedSummary {
  amount: number;
  deals: number;
  activationAmount: number;
  /** Always `amount - activationAmount`. */
  residualAmount: number;
  /** Same calendar window one year earlier. */
  priorYearAmount: number;
}

export interface CountAmount {
  count: number;
  amount: number;
}

export interface SnapshotTiles {
  activeApplications: number;
  requiringAction: number;
  pendingActivation: CountAmount;
  /** Equals the hero's `deals` / `amount`. */
  funded: CountAmount;
}

export type RequiringActionStatus =
  | BrokerStatus.SUBMISSION_INCOMPLETE
  | BrokerStatus.OFFER
  | BrokerStatus.CLOSING_INCOMPLETE
  | BrokerStatus.CONTRACT_READY;

/** The four statuses behind the Requiring action tile, the list and its "View all" filter. */
export const REQUIRING_ACTION_STATUSES: readonly RequiringActionStatus[] = [
  BrokerStatus.SUBMISSION_INCOMPLETE,
  BrokerStatus.OFFER,
  BrokerStatus.CLOSING_INCOMPLETE,
  BrokerStatus.CONTRACT_READY,
];

export interface RequiringActionRow {
  applicationId: number;
  /** Null when the application has no account. */
  businessName: string | null;
  amount: number;
  action: string;
  brokerStatus: RequiringActionStatus;
  /** Badge and ordering clock. */
  waitingSinceUtc: Date;
}

/** Both fields are null together: no qualifying submission yet (never "hide the panel"). */
export interface InactivitySummary {
  lastSubmissionUtc: Date | null;
  daysSinceLastSubmission: number | null;
}

export interface ExecutiveSnapshot {
  asOfUtc: Date;
  period: ExecutiveSnapshotPeriod;
  funded: FundedSummary;
  tiles: SnapshotTiles;
  /** At most five rows, longest wait first. */
  requiringAction: RequiringActionRow[];
  requiringActionTotal: number;
  inactivity: InactivitySummary;
}

export enum WaitBadgeTier {
  DANGER = 'danger',
  WARNING = 'warning',
  NEUTRAL = 'neutral',
}

export interface RequiringActionRowVm extends RequiringActionRow {
  waitDays: number;
  badgeTier: WaitBadgeTier;
  /** `digitsInfo` for the `currency` pipe: whole dollars, or both decimals — never one. */
  amountDigits: string;
}

export enum InactivityPanelVariant {
  WARNING = 'warning',
  EMPTY = 'empty',
}

export interface InactivityPanelVm {
  variant: InactivityPanelVariant;
  /** Null in the empty variant (no submission yet). */
  days: number | null;
  /** `min(days, 90) / 90 * 100`; 0 in the empty variant. */
  progressPercent: number;
}

/** `[queryParams]` of the "View all" link on the submissions list. */
export interface ViewAllQuery {
  brokerStatus: RequiringActionStatus[];
  firstAgent?: string;
}

export interface ExecutiveSnapshotDataVm {
  /** "month to date", "quarter to date", "year to date" or the formatted custom range. */
  periodTitle: string;
  /** "Funded (MTD)", "Funded (QTD)", "Funded (YTD)" or "Funded (custom range)". */
  fundedTileLabel: string;
  funded: FundedSummary;
  /** Carries its own comparison year ("Ahead of 2025 pace by $610K funded"). */
  paceLine: string;
  tiles: SnapshotTiles;
  rows: RequiringActionRowVm[];
  /** `requiringActionTotal > 0` — the list is capped at five, the link goes to the full bucket. */
  showViewAll: boolean;
  viewAllQuery: ViewAllQuery;
  /** Null only when the last submission is 0–29 days old. */
  inactivityPanel: InactivityPanelVm | null;
}

/** Re-exported in value position: components hold it as a template constant, so a type alias would not compile. */
export { DashboardVmState as ExecutiveSnapshotVmState } from './dashboard-vm.model';

export type ExecutiveSnapshotVm = DashboardVm<ExecutiveSnapshotDataVm>;
